import { useDebounceFn } from '@vueuse/core'
import { ref, type Ref } from 'vue'

import { RelationGroupType, RelationType } from '@/constants/relations'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import {
  computeNameSimilarity,
  normalizeOrganizationName,
} from '@/utilities/organizationNameSimilarity'

// eslint-disable-next-line import/exports-last
export interface OrganizationDuplicateMatch {
  id: string
  organizationName: string
  relationType: RelationType
}

type CheckForDuplicatesHandler = (name: string) => void
type VoidHandler = () => void

interface UseOrganizationDuplicateCheckReturn {
  checkForDuplicates: CheckForDuplicatesHandler
  duplicateMatches: Ref<OrganizationDuplicateMatch[]>
  isCheckingDuplicates: Ref<boolean>
  resetDuplicates: VoidHandler
}

// Minimum number of characters before a duplicate lookup is performed.
const MIN_QUERY_LENGTH = 2
// Number of candidates fetched from the backend for client-side scoring.
const CANDIDATE_FETCH_LIMIT = 25
// Maximum number of similar organizations shown in the warning.
const MAX_RESULTS = 5
// Similarity score (0..1) a candidate must reach to be treated as a duplicate.
// Lower = more matches (noisier), higher = stricter. Tuned against real data.
const SIMILARITY_THRESHOLD = 0.55
const DEBOUNCE_MS = 500

interface ScoredCandidate {
  match: OrganizationDuplicateMatch
  score: number
}

// Coarse backend prefilter term: the first meaningful word keeps the candidate
// set bounded while still surfacing the whole family of similar names, which
// substring-matching the full input would miss (e.g. "Mülichen Kompanie").
const buildFilterTerm = (trimmedName: string): string => {
  const firstToken = trimmedName.split(/\s+/u)[0] ?? trimmedName
  return firstToken.length >= MIN_QUERY_LENGTH ? firstToken : trimmedName
}

// eslint-disable-next-line func-style
export function useOrganizationDuplicateCheck(): UseOrganizationDuplicateCheckReturn {
  const customAPIStore = useFrappeNestCustomAPIStore()

  const duplicateMatches = ref<OrganizationDuplicateMatch[]>([])
  const isCheckingDuplicates = ref(false)

  const resetDuplicates = (): void => {
    duplicateMatches.value = []
  }

  const runCheck = async (name: string): Promise<void> => {
    const trimmedName = name.trim()
    const normalizedQuery = normalizeOrganizationName(trimmedName)
    if (trimmedName.length < MIN_QUERY_LENGTH || normalizedQuery.length === 0) {
      resetDuplicates()
      isCheckingDuplicates.value = false
      return
    }

    isCheckingDuplicates.value = true
    try {
      const organizations = await customAPIStore.getOrganizationList({
        fields: ['name', 'organization_name', 'doctype'],
        filters: {
          AND: [['organization_name', 'like', `%${buildFilterTerm(trimmedName)}%`]],
          OR: [],
        },
        limit: CANDIDATE_FETCH_LIMIT,
        sub_type: RelationType.all,
        type: RelationGroupType.organizations,
      })

      duplicateMatches.value = organizations
        .map(
          (organization): ScoredCandidate => ({
            match: {
              id: organization.name,
              organizationName: organization.organization_name,
              relationType: organization.doctype,
            },
            score: computeNameSimilarity(
              normalizedQuery,
              normalizeOrganizationName(organization.organization_name),
            ),
          }),
        )
        .filter((candidate) => candidate.score >= SIMILARITY_THRESHOLD)
        .sort((first, second) => second.score - first.score)
        .slice(0, MAX_RESULTS)
        .map((candidate) => candidate.match)
    } finally {
      isCheckingDuplicates.value = false
    }
  }

  const debouncedCheck = useDebounceFn(runCheck, DEBOUNCE_MS)

  const checkForDuplicates = (name: string): void => {
    resetDuplicates()
    if (name.trim().length < MIN_QUERY_LENGTH) {
      return
    }
    // eslint-disable-next-line no-void
    void debouncedCheck(name)
  }

  return { checkForDuplicates, duplicateMatches, isCheckingDuplicates, resetDuplicates }
}
